import { useState } from 'react';
import {
  useGetMessageToSign,
  useGetUserCardInfo,
} from '@xhype/card-app-sdk';
import { useAuthStore } from '@package/smart-wallet/store';

import { useSignMessage } from '../auth';

/* Types */
type UseGetInfoCardProps = {
  cardId: number;
  enabled?: boolean;
};

/* Hooks */
export const useGetInfoCard = ({
  cardId,
  enabled = true,
}: UseGetInfoCardProps) => {
  const id = useAuthStore((store) => store.id);
  const { signMessage } = useSignMessage();
  const [isFetchingSensitiveData, setIsFetchingSensitiveData] = useState(false);

  /* Queries */
  const { refetch: refetchMessage } = useGetMessageToSign({
    enabled: false, // We'll manually trigger this when needed
  });

  const {
    userCardInfo: cardInfo,
    loading,
    getSensitiveData,
    sensitiveData,
    error,
  } = useGetUserCardInfo({
    enabled: !!id && !!cardId && enabled,
    userId: id ?? 0,
    cardId,
  });

  const getSensitiveCardData = async () => {
    if (!id || !cardId) {
      throw new Error('Missing userId or cardId');
    }

    setIsFetchingSensitiveData(true);

    try {
      // Step 1: Get message to sign from backend
      const { data: messageToSignData } = await refetchMessage();

      if (!messageToSignData?.msgToSign) {
        throw new Error('Failed to get message to sign from backend');
      }

      const message = messageToSignData.msgToSign;

      // Step 2: Sign the message
      const signature = await signMessage(message);

      // Step 3: Get sensitive data with signature
      await getSensitiveData(signature, message);
    } catch (error) {
      console.error('Error fetching sensitive card data:', error);
      throw error;
    } finally {
      setIsFetchingSensitiveData(false);
    }
  };

  /* Return */
  return {
    isLoading: loading || isFetchingSensitiveData,
    isFetchingSensitiveData,
    cardInfo,
    getSensitiveData: getSensitiveCardData,
    sensitiveData,
    error,
  };
};
